import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send, Loader2, MessageSquare, X, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MESSAGE_LIMIT_BEFORE_SIGNUP = 5;

export default function EmbedWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [signupData, setSignupData] = useState({ name: '', password: '' });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [projectId, setProjectId] = useState(null);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Check for existing session
    const savedSession = localStorage.getItem('slp_widget_session');
    const savedToken = localStorage.getItem('slp_widget_token');
    if (savedSession) {
      const session = JSON.parse(savedSession);
      setSessionId(session.id);
      setEmail(session.email);
      setMessageCount(session.messageCount || 0);
    }
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      // Load existing project
      loadProject(savedToken);
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadProject = async (authToken) => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (response.data.length > 0) {
        const project = response.data[0];
        setProjectId(project.id);
        // Load messages
        const projResponse = await axios.get(`${API}/projects/${project.id}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        setMessages(projResponse.data.messages || []);
      }
    } catch (error) {
      console.error('Error loading project:', error);
    }
  };

  const startSession = async () => {
    if (!email.trim()) return;
    
    try {
      // Create or get widget session
      const response = await axios.post(`${API}/widget/session`, { email });
      const session = response.data;
      
      setSessionId(session.id);
      localStorage.setItem('slp_widget_session', JSON.stringify({
        id: session.id,
        email: email,
        messageCount: 0
      }));
      
      // Add welcome message
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: "G'day! I'm your change coach. What are you working on?"
      }]);
    } catch (error) {
      console.error('Error starting session:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return;
    
    // Check message limit
    if (!isAuthenticated && messageCount >= MESSAGE_LIMIT_BEFORE_SIGNUP) {
      setShowSignupPrompt(true);
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputMessage
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setSending(true);

    try {
      let response;
      if (isAuthenticated && projectId) {
        // Use authenticated endpoint
        response = await axios.post(`${API}/projects/${projectId}/chat`, {
          content: userMessage.content,
          project_id: projectId
        });
      } else {
        // Use widget endpoint
        response = await axios.post(`${API}/widget/chat`, {
          session_id: sessionId,
          content: userMessage.content
        });
      }
      
      setMessages(prev => [...prev, {
        id: response.data.id || `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.data.content
      }]);
      
      // Update message count
      const newCount = messageCount + 1;
      setMessageCount(newCount);
      
      // Update saved session
      const savedSession = JSON.parse(localStorage.getItem('slp_widget_session') || '{}');
      savedSession.messageCount = newCount;
      localStorage.setItem('slp_widget_session', JSON.stringify(savedSession));
      
      // Show signup prompt if approaching limit
      if (!isAuthenticated && newCount >= MESSAGE_LIMIT_BEFORE_SIGNUP - 1) {
        setTimeout(() => setShowSignupPrompt(true), 1000);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: "Sorry, something went wrong. Try again?"
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleSignup = async () => {
    if (!signupData.name.trim() || !signupData.password.trim()) return;
    
    try {
      // Register user
      const response = await axios.post(`${API}/auth/register`, {
        email: email,
        password: signupData.password,
        name: signupData.name
      });
      
      const authToken = response.data.token;
      setToken(authToken);
      setIsAuthenticated(true);
      localStorage.setItem('slp_widget_token', authToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      
      // Create a project and migrate conversation
      const projectResponse = await axios.post(`${API}/projects`, {
        name: 'My Change Project',
        description: ''
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      setProjectId(projectResponse.data.id);
      setShowSignupPrompt(false);
      
      // Add confirmation message
      setMessages(prev => [...prev, {
        id: `system-${Date.now()}`,
        role: 'assistant',
        content: "Account created! Your conversation is saved. Keep going - no limits now."
      }]);
    } catch (error) {
      console.error('Error signing up:', error);
      if (error.response?.data?.detail?.includes('already registered')) {
        // Try to login instead
        try {
          const loginResponse = await axios.post(`${API}/auth/login`, {
            email: email,
            password: signupData.password
          });
          const authToken = loginResponse.data.token;
          setToken(authToken);
          setIsAuthenticated(true);
          localStorage.setItem('slp_widget_token', authToken);
          setShowSignupPrompt(false);
          loadProject(authToken);
        } catch (loginError) {
          alert('Account exists but password is incorrect.');
        }
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="fixed bottom-20 right-4 z-50 font-sans">
      {/* Chat bubble button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            className="w-14 h-14 bg-slate-900 rounded-full shadow-lg flex items-center justify-center hover:bg-slate-800 transition-colors"
            data-testid="widget-open-btn"
          >
            <MessageSquare className="h-6 w-6 text-amber-400" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="w-[380px] h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
          >
            {/* Header */}
            <div className="bg-slate-900 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-slate-900" />
                </div>
                <div>
                  <h3 className="text-white font-medium text-sm">SLP Coach</h3>
                  <p className="text-slate-400 text-xs">Change management support</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-slate-800 rounded-lg transition-colors"
                data-testid="widget-close-btn"
              >
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>

            {/* Content */}
            {!sessionId && !isAuthenticated ? (
              /* Email entry */
              <div className="flex-1 flex flex-col items-center justify-center p-6">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                  <MessageSquare className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Start chatting</h3>
                <p className="text-sm text-slate-500 text-center mb-6">
                  Get quick guidance on your change initiative
                </p>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mb-3"
                  data-testid="widget-email-input"
                />
                <button
                  onClick={startSession}
                  disabled={!email.trim()}
                  className="w-full py-3 bg-slate-900 text-white rounded-xl font-medium text-sm hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="widget-start-btn"
                >
                  Start Chatting
                </button>
              </div>
            ) : showSignupPrompt ? (
              /* Signup prompt */
              <div className="flex-1 flex flex-col items-center justify-center p-6">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                  <User className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Create your account</h3>
                <p className="text-sm text-slate-500 text-center mb-6">
                  Save your progress and continue the conversation anytime
                </p>
                <input
                  type="text"
                  value={signupData.name}
                  onChange={(e) => setSignupData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Your name"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mb-3"
                  data-testid="widget-name-input"
                />
                <input
                  type="password"
                  value={signupData.password}
                  onChange={(e) => setSignupData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Create a password"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 mb-3"
                  data-testid="widget-password-input"
                />
                <button
                  onClick={handleSignup}
                  disabled={!signupData.name.trim() || !signupData.password.trim()}
                  className="w-full py-3 bg-slate-900 text-white rounded-xl font-medium text-sm hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="widget-signup-btn"
                >
                  Create Account
                </button>
                <p className="text-xs text-slate-400 mt-3">
                  {MESSAGE_LIMIT_BEFORE_SIGNUP - messageCount} messages remaining on free trial
                </p>
              </div>
            ) : (
              /* Chat interface */
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                          message.role === 'user'
                            ? 'bg-slate-900 text-white rounded-br-md'
                            : 'bg-slate-100 text-slate-700 rounded-bl-md'
                        }`}
                      >
                        {message.content}
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 px-4 py-2.5 rounded-2xl rounded-bl-md">
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message limit warning */}
                {!isAuthenticated && messageCount >= MESSAGE_LIMIT_BEFORE_SIGNUP - 2 && (
                  <div className="px-4 py-2 bg-amber-50 text-amber-700 text-xs text-center">
                    {MESSAGE_LIMIT_BEFORE_SIGNUP - messageCount} messages left • 
                    <button 
                      onClick={() => setShowSignupPrompt(true)}
                      className="underline ml-1"
                    >
                      Create account for unlimited
                    </button>
                  </div>
                )}

                {/* Input */}
                <div className="p-3 border-t border-slate-100">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Ask me anything..."
                      className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                      disabled={sending}
                      data-testid="widget-message-input"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!inputMessage.trim() || sending}
                      className="p-2.5 bg-slate-900 rounded-xl hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      data-testid="widget-send-btn"
                    >
                      <Send className="h-4 w-4 text-white" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
